/**
 * La navegación del portal del colaborador, una sola para los dos layouts
 * (cuenta del portal y usuario del ERP). Antes cada layout declaraba su propia
 * barra y las dos se desalineaban.
 *
 * Secciones (plan del portal, D2): Inicio · Documentos · Vacaciones y permisos ·
 * Organigrama, y «Mi equipo» solo para quien tiene equipo. Hasta que la fase 5
 * junte liquidaciones, carpeta y firmas en una sola lista, «Documentos» agrupa
 * esas tres pantallas y las ofrece en una segunda fila.
 *
 * ⚠️ Las rutas viejas (`/payslips`, `/signatures?firmar=`) se conservan: son las
 * que llevan los correos ya enviados.
 */
import { NavLink, useLocation } from 'react-router-dom'
import { usePortalBase, usePortalInicio } from '../hooks/useEmployeePortal'
import { SECCIONES_DEL_PORTAL, seccionActiva, tramo } from './seccionesDelPortal'

const enlace = (activo: boolean) =>
  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
    activo ? 'bg-selected text-primary font-semibold' : 'text-content-muted hover:bg-hover hover:text-content'
  }`

export function PortalNav() {
  const base = usePortalBase()
  const { pathname } = useLocation()
  const activa = seccionActiva(pathname, base)
  const actual = tramo(pathname, base)
  const { data: inicio } = usePortalInicio()
  // «Mi equipo» solo a quien tiene equipo; y siempre si ya se está en esa pantalla
  // (un enlace directo no puede dejar la pestaña activa sin mostrarse).
  const secciones = SECCIONES_DEL_PORTAL.filter(
    s => !s.soloConEquipo || inicio?.tiene_equipo || s === activa)
  const porResolver = inicio?.aprobaciones_pendientes ?? 0

  return (
    <div className="border-b border-divider bg-surface-muted">
      <nav className="flex gap-1 px-4 sm:px-6 py-2 overflow-x-auto" aria-label="Secciones del portal">
        {secciones.map(s => {
          const destino = s.rutas[0].ruta
          const esta = s === activa
          const Icono = s.icono
          return (
            <NavLink
              key={s.rotulo}
              to={destino ? `${base}/${destino}` : base}
              end={!destino}
              className={() => enlace(esta)}
              aria-current={esta ? 'page' : undefined}
            >
              <Icono size={15} aria-hidden="true" />
              {s.rotuloCorto
                ? <><span className="sm:hidden">{s.rotuloCorto}</span><span className="hidden sm:inline">{s.rotulo}</span></>
                : s.rotulo}
              {s.soloConEquipo && porResolver > 0 && (
                <span className="ml-1 min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-primary text-on-primary text-[11px] font-semibold text-center"
                  aria-label={`${porResolver} solicitudes por resolver`}>
                  {porResolver}
                </span>
              )}
            </NavLink>
          )
        })}
      </nav>
      {activa && activa.rutas.length > 1 && (
        <nav className="flex gap-1 px-4 sm:px-6 pb-2 overflow-x-auto" aria-label={`Pantallas de ${activa.rotulo}`}>
          {activa.rutas.map(r => {
            const esta = r.ruta === actual
            return (
              <NavLink
                key={r.ruta || 'inicio'}
                to={r.ruta ? `${base}/${r.ruta}` : base}
                end
                aria-current={esta ? 'page' : undefined}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                  esta ? 'bg-surface text-content font-semibold border border-divider' : 'text-content-muted hover:text-content'
                }`}
              >
                {r.rotulo}
              </NavLink>
            )
          })}
        </nav>
      )}
    </div>
  )
}
